import { ReactNode, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { useSiteAdmin } from '@/hooks/useSiteAdmin';
import { Button } from '@/components/ui/button';
import { SiteSwitcher } from '@/components/admin/SiteSwitcher';
import { 
  Home, LogOut, Settings, User, FileText, MapPin, Building, 
  Users, Landmark, LayoutDashboard, Wand2, Image, PenTool
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AdminLayoutProps {
  children: ReactNode;
  title?: string;
  showNav?: boolean;
}

interface NavItem {
  label: string;
  href: string;
  icon: ReactNode;
  globalAdminOnly?: boolean;
  requiresWizardComplete?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/admin', icon: <LayoutDashboard className="h-4 w-4" /> },
  { label: 'Setup Wizard', href: '/admin/wizard', icon: <Wand2 className="h-4 w-4" /> },
  { label: 'Profile', href: '/admin/profile', icon: <User className="h-4 w-4" /> },
  { label: 'Settings', href: '/admin/settings', icon: <Settings className="h-4 w-4" /> },
  { label: 'Towns', href: '/admin/towns', icon: <MapPin className="h-4 w-4" />, requiresWizardComplete: false },
  { label: 'Areas', href: '/admin/areas', icon: <Building className="h-4 w-4" />, requiresWizardComplete: false },
  { label: 'Blog', href: '/admin/blog', icon: <FileText className="h-4 w-4" />, requiresWizardComplete: false },
  { label: 'Ads', href: '/admin/ads', icon: <Image className="h-4 w-4" />, requiresWizardComplete: false },
  { label: 'Leads', href: '/admin/leads', icon: <Users className="h-4 w-4" /> },
  { label: 'Loan Officers', href: '/admin/loan-officers', icon: <Landmark className="h-4 w-4" /> },
];

export function AdminLayout({ children, title, showNav = true }: AdminLayoutProps) {
  const { user, signOut, loading: authLoading } = useAuth();
  const { isGlobalAdmin, selectedSite, loading: globalAdminLoading, checked: globalAdminChecked } = useGlobalAdmin();
  const { site, isWizardComplete, loading: siteAdminLoading } = useSiteAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoading = authLoading || globalAdminLoading || siteAdminLoading || !globalAdminChecked;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login');
  };

  // Determine display site name
  const siteName = isGlobalAdmin ? selectedSite?.name : site?.name;

  // Filter nav items based on role
  const visibleNavItems = navItems.filter(item => {
    // Global admins see everything except wizard (they use switcher instead)
    if (isGlobalAdmin) {
      return item.href !== '/admin/wizard';
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-muted">
      <Helmet>
        <title>{title ? `${title} | ` : ''}{siteName || 'Admin'}</title>
      </Helmet>

      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Link to="/admin" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <Home className="h-5 w-5 text-primary" />
                <span className="font-display text-lg">{siteName || 'Dashboard'}</span>
              </Link>
              {isGlobalAdmin && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                  Global Admin
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <SiteSwitcher />
              <span className="text-sm text-muted-foreground hidden sm:block">{user.email}</span>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      {showNav && (
        <nav className="bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-1 overflow-x-auto py-2">
              {visibleNavItems.map((item) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== '/admin' && location.pathname.startsWith(item.href));
                
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors",
                      isActive 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Wizard Incomplete Banner for Site Admins */}
      {!isGlobalAdmin && !isWizardComplete && location.pathname !== '/admin/wizard' && (
        <div className="bg-amber-50 border-b border-amber-200 dark:bg-amber-950 dark:border-amber-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-800 dark:text-amber-200">
                  Complete the Setup Wizard to publish your site
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/admin/wizard')}>
                Continue Setup
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>
    </div>
  );
}
